/**
 * types.ts — Types publics du module catalog (Phase D2b Round 1 + Phase D4c).
 *
 * Entités couvertes : products, categories, tenant_universes.
 *
 * Entités non disponibles dans le schéma actuel (scope_reduit_note) :
 *   - product_variants  : table absente de integrations/supabase/types.ts
 *   - product_groups    : table absente de integrations/supabase/types.ts
 *   - menus             : table absente de integrations/supabase/types.ts
 *   - recipes           : table absente de integrations/supabase/types.ts
 *
 * Tous les types sont dérivés STRICTEMENT de Database['public']['Tables'].
 * Aucune duplication du pattern D1-R4.
 *
 * Invariants :
 *   4.A.10 — tenant_id jamais dans les inputs du caller pour les opérations
 *             tenant-propres (RLS côté serveur). Pour les opérations cross-tenant
 *             super_admin, le tenantId cible est passé explicitement (acceptable
 *             car l'EF/RPC valide le rôle de l'appelant).
 *   4.B.4  — 0 any, types stricts dérivés de Database['public']['Tables'].
 */

import { z } from 'zod';
import type { Database } from '@/integrations/supabase/types';
import { PRINTER_FUNCTION_SLUG_REGEX } from '@/api/printing/printer-functions';

// ---------------------------------------------------------------------------
// Row types — dérivés de Database (single source of truth)
// ---------------------------------------------------------------------------

export type ProductRow = Database['public']['Tables']['products']['Row'];
export type ProductInsert = Database['public']['Tables']['products']['Insert'];
export type ProductUpdate = Database['public']['Tables']['products']['Update'];

export type CategoryRow = Database['public']['Tables']['categories']['Row'];
export type CategoryInsert = Database['public']['Tables']['categories']['Insert'];
export type CategoryUpdate = Database['public']['Tables']['categories']['Update'];

export type UniverseRow = Database['public']['Tables']['tenant_universes']['Row'];
export type UniverseInsert = Database['public']['Tables']['tenant_universes']['Insert'];
export type UniverseUpdate = Database['public']['Tables']['tenant_universes']['Update'];

// ---------------------------------------------------------------------------
// Zod schemas — input validation pour create/update
// ---------------------------------------------------------------------------

/**
 * Schema de création d'un produit.
 *
 * Invariant 4.A.10 : tenant_id absent — résolu par RLS côté serveur.
 * Invariant 4.B.4  : pas de any, types stricts.
 */
export const createProductSchema = z.object({
  name: z.string().min(1).max(255),
  price: z.number().nonnegative(),
  tva_rate: z.number().nonnegative().max(100).default(20),
  category_id: z.string().uuid().nullable().optional(),
  barcode: z.string().max(64).nullable().optional(),
  alternate_barcodes: z.array(z.string().min(1).max(64)).max(20).optional(),
  image_url: z.string().url().nullable().optional(),
  ingredients: z.string().nullable().optional(),
  allergens: z.record(z.string(), z.unknown()).nullable().optional(),
  stock: z.number().int().nonnegative().default(0),
  active: z.boolean().default(true),
  universe_id: z.string().uuid().nullable().optional(),
  sort_order: z.number().int().nonnegative().nullable().optional(),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;

/**
 * Schema de mise à jour d'un produit.
 * Tous les champs sont optionnels.
 */
export const updateProductSchema = createProductSchema.partial();
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

/**
 * Schema de création d'une catégorie.
 *
 * Invariant 4.A.10 : tenant_id absent — résolu par RLS côté serveur.
 */
export const createCategorySchema = z.object({
  name: z.string().min(1).max(255),
  icon: z.string().min(1).max(64).default('tag'),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Couleur hex invalide')
    .default('#6B7280'),
  sort_order: z.number().int().nonnegative().default(0),
  universe_id: z.string().uuid().nullable().optional(),
  /** Routage des bons par fonction d'imprimante (miroir du CHECK DB, lot 4b). */
  printer_function: z
    .string()
    .regex(PRINTER_FUNCTION_SLUG_REGEX, 'Fonction invalide : slug ascii minuscule attendu')
    .nullable()
    .optional(),
});
export type CreateCategoryInput = z.infer<typeof createCategorySchema>;

/**
 * Schema de mise à jour d'une catégorie.
 * Tous les champs sont optionnels.
 * `color` accepte null (retrait de couleur — consumer CategoryColorPickerDialog).
 */
export const updateCategorySchema = createCategorySchema.partial().extend({
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Couleur hex invalide')
    .nullable()
    .optional(),
});
export type UpdateCategoryInput = z.infer<typeof updateCategorySchema>;

/**
 * Schema de création d'un univers (tenant_universes).
 *
 * Invariant 4.A.10 : tenant_id absent des inputs — résolu par RLS côté serveur.
 * Note : tenant_id est Required dans TenantUniversesInsert ; il est injecté
 *   via la contrainte RLS NOT NULL from JWT, pas du caller.
 *   Pour l'insert direct Supabase, tenant_id est fourni via la session JWT RLS.
 */
export const createUniverseSchema = z.object({
  label: z.string().min(1).max(255),
  slug: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9-]+$/, 'Slug invalide (minuscules, chiffres et tirets uniquement)'),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Couleur hex invalide')
    .nullable()
    .optional(),
  icon: z.string().min(1).max(64).nullable().optional(),
  sort_order: z.number().int().nonnegative().default(0),
  is_active: z.boolean().default(true),
});
export type CreateUniverseInput = z.infer<typeof createUniverseSchema>;

/**
 * Schema de mise à jour d'un univers.
 */
export const updateUniverseSchema = createUniverseSchema.partial();
export type UpdateUniverseInput = z.infer<typeof updateUniverseSchema>;

// ---------------------------------------------------------------------------
// Filtres list
// ---------------------------------------------------------------------------

export type ListProductsFilters = {
  /** Filtrer par catégorie. */
  readonly categoryId?: string;
  /** Inclure les produits archivés (active = false). Défaut : false. */
  readonly includeArchived?: boolean;
  /** Filtrer par univers. */
  readonly universeId?: string;
  /**
   * UUID du tenant cible pour les requêtes cross-tenant super_admin.
   *
   * Invariant 4.A.10 (exception documentée) : paramètre cible super_admin.
   * Absent = filtre par le tenant de l'utilisateur courant via RLS.
   * Présent = filtre explicite par tenant_id (query Supabase, bypass RLS
   * uniquement si la policy le permet pour super_admin/service_role).
   *
   * Attention : ce paramètre ne contourne PAS RLS. Il ajoute un filtre
   * .eq('tenant_id', tenantId) EN PLUS de la RLS. Pour qu'il fonctionne
   * en cross-tenant, la RLS doit autoriser la lecture cross-tenant pour
   * les super_admin (via get_my_role() = 'super_admin' dans la policy).
   */
  readonly tenantId?: string;
};

export type ListCategoriesFilters = {
  /** Filtrer par univers. */
  readonly universeId?: string;
  /**
   * UUID du tenant cible pour les requêtes cross-tenant super_admin.
   * Même sémantique que ListProductsFilters.tenantId.
   */
  readonly tenantId?: string;
};

export type ListUniversesFilters = {
  /** Inclure les univers inactifs. Défaut : false. */
  readonly includeInactive?: boolean;
};

// ---------------------------------------------------------------------------
// Catalog import (Phase D4c) — EF catalog-import
// ---------------------------------------------------------------------------

/**
 * Actions supportées par l'EF `catalog-import`.
 *
 * - scan-menu  : scan d'une image de menu (base64 + mime)
 * - scan-pdf   : extraction texte d'un PDF menu
 * - commit     : validation + insertion des produits scannés
 * - rollback   : annulation d'un import (soft-delete des produits créés)
 * - quota-status : statut du quota journalier de scans
 */
export type CatalogImportAction = 'scan-menu' | 'scan-pdf' | 'commit' | 'rollback' | 'quota-status';

/**
 * Input pour OnboardingApi.importCatalog() — action 'scan-menu'.
 *
 * Invariant 4.A.10 (exception documentée) : tenant_id cible super_admin.
 * Invariant 4.A.15 : imageBase64 n'est pas un PII mais peut contenir des données
 *   confidentielles — ne pas logger.
 */
export const importCatalogScanMenuInputSchema = z.object({
  /** UUID du tenant cible. */
  tenantId: z.string().uuid(),
  /** Image encodée en base64. */
  imageBase64: z.string().min(1),
  /** MIME type de l'image (ex: 'image/jpeg'). */
  mimeType: z.string().min(1),
  /** Nom original du fichier (pour traçabilité). */
  fileName: z.string().min(1),
});
export type ImportCatalogScanMenuInput = z.infer<typeof importCatalogScanMenuInputSchema>;

/**
 * Input pour OnboardingApi.importCatalog() — action 'scan-pdf'.
 */
export const importCatalogScanPdfInputSchema = z.object({
  /** UUID du tenant cible. */
  tenantId: z.string().uuid(),
  /** Texte extrait du PDF. */
  pdfText: z.string().min(1),
  /** Nom original du fichier. */
  fileName: z.string().min(1),
});
export type ImportCatalogScanPdfInput = z.infer<typeof importCatalogScanPdfInputSchema>;

/** Union des inputs de scan catalog import. */
export type ImportCatalogScanInput = ImportCatalogScanMenuInput | ImportCatalogScanPdfInput;

/**
 * Résultat d'un scan catalog (scan-menu ou scan-pdf).
 *
 * Correspond au champ data retourné par l'EF catalog-import.
 * importId est nécessaire pour le commit et le rollback.
 */
export const importCatalogScanResultSchema = z.object({
  importId: z.string().min(1),
  scanned: z.object({
    products: z.array(
      z.object({
        name: z.string(),
        price: z.number().nonnegative(),
        category: z.string().optional(),
        tva_rate: z.number().nonnegative().optional(),
      }),
    ),
  }),
});
export type ImportCatalogScanResult = z.infer<typeof importCatalogScanResultSchema>;

/**
 * Input pour CatalogApi.rollbackCatalogImport().
 */
export const rollbackCatalogImportInputSchema = z.object({
  /** UUID du tenant cible. */
  tenantId: z.string().uuid(),
  /** ID de l'import à annuler (retourné par importCatalog). */
  importId: z.string().min(1),
});
export type RollbackCatalogImportInput = z.infer<typeof rollbackCatalogImportInputSchema>;

/**
 * Résultat du rollback d'un import catalog.
 */
export const rollbackCatalogImportResultSchema = z.object({
  /** Nombre de produits désactivés (soft-delete NF525-safe). */
  productsDeactivated: z.number().int().nonnegative(),
  /** Nombre de catégories supprimées. */
  categoriesDeleted: z.number().int().nonnegative(),
});
export type RollbackCatalogImportResult = z.infer<typeof rollbackCatalogImportResultSchema>;

/**
 * Input pour CatalogApi.getCatalogImportQuota().
 */
export const getCatalogImportQuotaInputSchema = z.object({
  tenantId: z.string().uuid(),
});
export type GetCatalogImportQuotaInput = z.infer<typeof getCatalogImportQuotaInputSchema>;

/**
 * Statut du quota journalier de scans catalog.
 */
export const catalogImportQuotaResultSchema = z.object({
  used: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
});
export type CatalogImportQuotaResult = z.infer<typeof catalogImportQuotaResultSchema>;

// ---------------------------------------------------------------------------
// Alias exports lisibles
// ---------------------------------------------------------------------------

/** Produit catalog. */
export type Product = ProductRow;

/** Catégorie catalog. */
export type Category = CategoryRow;

/** Univers de regroupement de catégories/produits. */
export type Universe = UniverseRow;
